import { observable } from 'mobx';

import { SerializedData, Store, AnalyticsStore, RouteStore, ContextStore } from 'bernie-core';

import {
  AppliedFiltersSummaryType,
  FlightsSortAndFilterApplyAction,
  FilterPresentationType,
  FlightsSortAndFilterRevealAction,
  SortPresentationType,
  FlightsFilterLoadedOptionType,
  LoadedSortPresentationType,
  FlightsFilterOptionType
} from 'types/search/sort-filter';
import { AnalyticsType, IconType, FlightsActionableLabel, FlightsFilterItemType } from 'types/shared';
import analyticsWrapper from 'utility/analytics-wrapper';
import { SearchCriteriaStore, AdvertisingStore } from '.';

export class SortFilterStore extends Store {
  @observable public appliedFiltersSummary: AppliedFiltersSummaryType;
  @observable public applyAction: FlightsSortAndFilterApplyAction;
  @observable public clearSelectionLabel: FlightsActionableLabel;
  @observable public clearAllSelectionLabel: FlightsActionableLabel;
  @observable public dismiss: IconType;
  @observable public dismissAnalytics: AnalyticsType;
  @observable public filterPresentation?: FilterPresentationType;
  @observable public revealAction: FlightsSortAndFilterRevealAction;
  @observable public revealActionAnalytics: AnalyticsType;
  @observable public sortPresentation: SortPresentationType;
  @observable public title: string;
  @observable public loading: boolean;
  public actionInProgress = false;
  public initialFilterSkeletons: FlightsFilterOptionType[];

  public hydrate(data: SerializedData): void {
    Object.assign(this, data);
    this.actionInProgress = false;
    this.loading = false;
  }

  public hasSortApplied(): boolean {
    const sortPresentation = this.sortPresentation as LoadedSortPresentationType;

    return (
      sortPresentation.selectedOption &&
      sortPresentation.options &&
      sortPresentation.selectedOption.label !== sortPresentation.options[0].label
    );
  }

  public applySortAndFilter(
    searchCriteriaStore: SearchCriteriaStore,
    routeStore: RouteStore,
    advertisingStore: AdvertisingStore,
    context: ContextStore,
    analytics: AnalyticsStore
  ) {
    advertisingStore.showCLAds(false);
    const { searchCriteria } = searchCriteriaStore;
    const { sortPresentation } = this;
    searchCriteriaStore.syncWithSortAndFilterStore(this);
    searchCriteria.sortOption = sortPresentation.selectedOption.sortType;
    searchCriteriaStore.triggerSearch(routeStore, context, true, false, false);

    if (!this.actionInProgress) {
      this.actionInProgress = true;
      analyticsWrapper.resetSearchResultsAnalyticsPayLoad(analytics);
    }
    advertisingStore.displayAdRefresh();
  }

  public clearFilters(): void {
    const options = this.filterPresentation && this.filterPresentation.options;

    options &&
      options.forEach((filterOption: FlightsFilterLoadedOptionType): void => {
        filterOption.items &&
          filterOption.items.forEach((filterItem: FlightsFilterItemType): void => {
            filterItem.selected = false;
          });
      });

    this.revealAction.appliedFilterCount = 0;
    if (this.appliedFiltersSummary) {
      this.appliedFiltersSummary.appliedFilters = [];
    }
  }

  public hasFiltersApplied(): boolean {
    return this.revealAction.appliedFilterCount > 0;
  }

  public hasSortOrFilterApplied(): boolean {
    return this.hasFiltersApplied() || this.hasSortApplied();
  }

  public increaseAppliedFilterCount(): void {
    this.revealAction.appliedFilterCount++;
  }

  public decreaseAppliedFilterCount(): void {
    this.revealAction.appliedFilterCount--;
  }

  public saveInitialSkeletons(): void {
    const filterSkeletons =
      this.filterPresentation &&
      this.filterPresentation.options &&
      this.filterPresentation.options.filter(option => option.__typename === 'FlightsFilterSkeletonOption');

    if (filterSkeletons && filterSkeletons.length > 0) {
      this.initialFilterSkeletons = filterSkeletons;
    }
  }

  public addSkeletonsOnLoading(): void {
    if (this.loading) {
      if (this.sortPresentation) {
        this.sortPresentation.__typename = 'FlightsSortLoadingPresentation';
      }
      if (!this.filterPresentation) {
        this.filterPresentation = this.defaultLoadingFilterPresentation();
      }
      this.filterPresentation.options = this.initialFilterSkeletons;
      this.filterPresentation.__typename = 'FlightsFilterLoadingPresentation';

      if (this.appliedFiltersSummary) {
        this.appliedFiltersSummary.appliedFilters = [];
      }
    }
  }

  private defaultLoadingFilterPresentation(): FilterPresentationType {
    return { options: null, __typename: 'FlightsFilterLoadingPresentation', accessibility: null, title: null };
  }
}

SortFilterStore.DEFAULTS = {
  loading: true
};
